import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

/* --- SVG Icons --- */
const IconLogout = (props) => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" {...props}>
    <path d="M6 2H3.5A1.5 1.5 0 0 0 2 3.5v9A1.5 1.5 0 0 0 3.5 14H6M10.5 11.5L14 8L10.5 4.5M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconSearch = (props) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.7" />
    <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const IconHistory = (props) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconTemplate = (props) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const INITIAL_EMAIL_TEMPLATE = `<p>Hi <strong>{{Contact_Person}}</strong>,</p>
<p>We’re pleased to inform you that the new functionality has been successfully implemented for <strong>{{Company_Name}}</strong>.<br>
Your registered GST Number is <strong>{{GST_Number}}</strong>.</p>
<p>Please review the update and share your feedback or let us know if any additional changes are required.</p>
<p>Thank you for your support and cooperation.</p>`;

export default function VendorEmailSender() {
  const navigate = useNavigate();
  const location = useLocation();

  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  
  const [industryFilter, setIndustryFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [availableIndustries, setAvailableIndustries] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [subject, setSubject] = useState('New implementation of functionality');
  
  // Editor state & refs
  const quillRef = useRef(null);
  const editorInstance = useRef(null);
  const [bodyHtml, setBodyHtml] = useState(INITIAL_EMAIL_TEMPLATE);

  // Template Management States
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [showTemplateDrawer, setShowTemplateDrawer] = useState(false);

  const [attachmentFile, setAttachmentFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    if (quillRef.current && !editorInstance.current) {
      editorInstance.current = new Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ header: [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image'],
            ['clean']
          ]
        },
        placeholder: 'Type plain text, apply styling, or paste images here...'
      });

      editorInstance.current.root.innerHTML = INITIAL_EMAIL_TEMPLATE;

      editorInstance.current.on('text-change', () => {
        setBodyHtml(editorInstance.current.root.innerHTML);
      });
    }
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await fetch('/api/email-templates', { credentials: 'include' });
        const data = await res.json();
        if (data.success && data.templates) {
          setSavedTemplates(data.templates);
        } else {
          const localTmpls = JSON.parse(localStorage.getItem('synergy_email_templates') || '[]');
          setSavedTemplates(localTmpls);
        }
      } catch (err) {
        const localTmpls = JSON.parse(localStorage.getItem('synergy_email_templates') || '[]');
        setSavedTemplates(localTmpls);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (location.state?.preselectedVendor) {
      const v = location.state.preselectedVendor;
      setSelectedIds([v.Id]);
      if (location.state.defaultSubject) {
        setSubject(location.state.defaultSubject);
      }
    }
  }, [location.state]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await fetch('/api/vendor-filters', { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setAvailableIndustries(data.industries || []);
          setAvailableCategories(data.categories || []);
        }
      } catch (err) {
        console.error('Error loading filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  const fetchVendors = useCallback(async () => {
    setFetching(true);
    try {
      const queryParams = new URLSearchParams({ search, industry: industryFilter, category: categoryFilter });
      const res = await fetch(`/api/vendors?${queryParams.toString()}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setVendors(data.data);
      }
    } catch (err) {
      console.error('Error fetching vendors:', err);
    } finally {
      setFetching(false);
    }
  }, [search, industryFilter, categoryFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchVendors();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, industryFilter, categoryFilter, fetchVendors]);

  const toggleSelectVendor = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === vendors.length && vendors.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(vendors.map(v => v.Id));
    }
  };

  const insertToken = (token) => {
    if (editorInstance.current) {
      const range = editorInstance.current.getSelection() || { index: editorInstance.current.getLength() };
      editorInstance.current.insertText(range.index, ` ${token} `);
    }
  };

  const handleInsertMyDetails = () => {
    const myDetailsBlock = `<p><br></p><p><strong>Ajay Bhai</strong><br>CEO | Synergy5M LLP<br>501, Fortuna Business Centre, Opp. McDonald's,<br>Pimple Saudagar, Pune, Maharashtra, India. PIN - 411027<br>Website: <a href="https://synergy5m.com">synergy5m.com</a><br>Contact: +91-9423579446</p>`;
    if (editorInstance.current) {
      const range = editorInstance.current.getSelection() || { index: editorInstance.current.getLength() };
      editorInstance.current.clipboard.dangerouslyPasteHTML(range.index, myDetailsBlock);
    }
  };

  const handleSaveTemplateSubmit = async (e) => {
    e.preventDefault();
    if (!templateNameInput.trim()) return;

    const newTemplateItem = {
      id: Date.now(),
      name: templateNameInput.trim(),
      subject: subject,
      bodyHtml: bodyHtml,
      updatedAt: new Date().toISOString()
    };

    const existingIndex = savedTemplates.findIndex(t => t.subject.toLowerCase() === subject.toLowerCase());
    let updatedList = [...savedTemplates];
    if (existingIndex >= 0) {
      updatedList[existingIndex] = { ...updatedList[existingIndex], name: templateNameInput.trim(), bodyHtml: bodyHtml, updatedAt: new Date().toISOString() };
    } else {
      updatedList.unshift(newTemplateItem);
    }

    setSavedTemplates(updatedList);
    localStorage.setItem('synergy_email_templates', JSON.stringify(updatedList));

    setShowTemplateModal(false);
    setTemplateNameInput('');
    setStatusMessage({ type: 'success', text: `Template "${templateNameInput}" successfully saved!` });
  };

  const handleApplyTemplate = (tmpl) => {
    setSubject(tmpl.subject);
    setBodyHtml(tmpl.bodyHtml);
    if (editorInstance.current) {
      editorInstance.current.root.innerHTML = tmpl.bodyHtml;
    }
    setShowTemplateDrawer(false);
    setStatusMessage({ type: 'success', text: `Loaded template: "${tmpl.name}"` });
  };

  const handleSendEmails = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) {
      alert('Please select at least one vendor to send emails.');
      return;
    }

    setLoading(true);
    setStatusMessage(null);

    const formData = new FormData();
    formData.append('vendorIds', JSON.stringify(selectedIds));
    formData.append('subject', subject);
    formData.append('bodyTemplate', bodyHtml);
    if (attachmentFile) {
      formData.append('attachment', attachmentFile);
    }

    try {
      const res = await fetch('/api/send-vendor-emails-attachment', {
        method: 'POST',
        credentials: 'include',
        body: formData 
      });
      
      const data = await res.json();
      if (data.success) {
        setStatusMessage({ type: 'success', text: data.message });
        setSelectedIds([]);
        setAttachmentFile(null);
      } else {
        setStatusMessage({ type: 'error', text: data.message || 'Failed to dispatch emails.' });
      }
    } catch (err) {
      console.error('Network error:', err);
      setStatusMessage({ type: 'error', text: 'Network or server error occurred during dispatch.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.dashboardContainer}>
      <GlobalStyle />
      
      <div style={styles.topBar}>
        <div style={styles.topBarLeft}>
          <div style={styles.brandMarkSmall}>S5</div>
          <div>
            <div style={styles.topBarTitle}>Synergy 5M</div>
            <div style={styles.topBarSubtitle}>Vendor Email Dispatch Center</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button type="button" onClick={() => setShowTemplateDrawer(true)} style={styles.secondaryActionBtn}>
            <IconTemplate /> Saved Templates ({savedTemplates.length})
          </button>
          <button type="button" onClick={() => navigate('/admin/email-history')} style={styles.logoutBtn}>
            <IconHistory /> View History 
          </button>
          <button type="button" onClick={() => navigate('/admin')} style={styles.logoutBtn}>
            <IconLogout /> Admin Panel
          </button>
        </div>
      </div>

      <div style={styles.pageBody}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "20px", flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#14181C", margin: "0 0 4px 0" }}>Broadcast Rich-Text Emails</h1>
            <p style={{ fontSize: "13.5px", color: "#5B6570", margin: 0 }}>Filter target vendors, customize templates, and dispatch branded communications.</p>
          </div>
          <button 
            type="button" 
            onClick={() => { setTemplateNameInput(subject); setShowTemplateModal(true); }} 
            style={styles.saveTemplateTopBtn}
          >
            💾 Save Current as Template
          </button>
        </div>

        {statusMessage && (
          <div style={{
            padding: "12px 16px", marginBottom: "20px", borderRadius: "8px", fontSize: "13.5px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: statusMessage.type === 'success' ? '#E3F3E8' : '#FBEAE9',
            color: statusMessage.type === 'success' ? '#1E7B45' : '#C4362E',
            border: `1px solid ${statusMessage.type === 'success' ? '#B8E7C5' : '#F1C7C4'}`
          }}>
            <span>{statusMessage.text}</span>
            <button onClick={() => setStatusMessage(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '12px', color: 'inherit', marginLeft: 'auto' }}>
              DISMISS
            </button>
          </div>
        )}

        <div className="ap-email-grid" style={styles.emailDispatchGrid}>
          
          {/* LEFT PANEL: Vendor Selection Grid */}
          <div style={styles.emailLeftCard}>
            <div style={{ padding: "16px", borderBottom: "1px solid #E4E7E9", background: "#FAFBFB" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#14181C", margin: 0 }}>
                  Select Vendors ({selectedIds.length} chosen)
                </h3>
              </div>
              
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <IconSearch color="#788693" style={{ position: "absolute", left: "10px" }} />
                  <input 
                    type="text"
                    placeholder="Search company, email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="ap-input"
                    style={{ width: "100%", padding: "8px 10px 8px 32px", borderRadius: "6px", border: "1px solid #D8DCE0", fontSize: "13px", background: "#fff" }}
                  />
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <select
                    value={industryFilter}
                    onChange={(e) => setIndustryFilter(e.target.value)}
                    className="ap-input"
                    style={{ width: "50%", padding: "8px 10px", borderRadius: "6px", border: "1px solid #D8DCE0", fontSize: "12.5px", background: "#fff", cursor: "pointer" }}
                  >
                    <option value="">All Industries</option>
                    {availableIndustries.map((ind, idx) => (<option key={idx} value={ind}>{ind}</option>))}
                  </select>

                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="ap-input"
                    style={{ width: "50%", padding: "8px 10px", borderRadius: "6px", border: "1px solid #D8DCE0", fontSize: "12.5px", background: "#fff", cursor: "pointer" }}
                  >
                    <option value="">All Categories</option>
                    {availableCategories.map((cat, idx) => (<option key={idx} value={cat}>{cat}</option>))}
                  </select>
                </div>
              </div>
            </div>

            <div style={{ maxHeight: "500px", overflowY: "auto", padding: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", paddingBottom: "10px", marginBottom: "8px", borderBottom: "1px solid #EEF0F1", position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
                <input 
                  type="checkbox"
                  checked={vendors.length > 0 && selectedIds.length === vendors.length}
                  onChange={toggleSelectAll}
                  style={{ width: "15px", height: "15px", cursor: "pointer" }}
                />
                <span style={{ marginLeft: "10px", fontSize: "12.5px", fontWeight: 600, color: "#5B6570" }}>
                  Select All Visible ({vendors.length})
                </span>
              </div>

              {fetching ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#9AA5AF", fontSize: "13px" }}>Loading vendors...</div>
              ) : vendors.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#9AA5AF", fontSize: "13px" }}>No vendors found.</div>
              ) : (
                vendors.map(vendor => {
                  const isChecked = selectedIds.includes(vendor.Id);
                  return (
                    <div 
                      key={vendor.Id} 
                      onClick={() => toggleSelectVendor(vendor.Id)}
                      style={{
                        display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px", borderRadius: "6px", cursor: "pointer",
                        background: isChecked ? "#E4EEEC" : "transparent", marginBottom: "6px", border: "1px solid", borderColor: isChecked ? "#14524A" : "transparent",
                      }}
                    >
                      <input type="checkbox" checked={isChecked} onChange={() => {}} style={{ marginTop: "2px", cursor: "pointer" }} />
                      <div style={{ fontSize: "13px", width: "100%" }}>
                        <div style={{ fontWeight: 600, color: "#14181C" }}>{vendor.Company_Name}</div>
                        <div style={{ color: "#5B6570", fontSize: "12px" }}>{vendor.Email || "No Email Listed"}</div>
                        <div style={{ color: "#788693", fontSize: "11px", marginTop: "2px" }}>
                          {vendor.industry && <span>Ind: {vendor.industry} </span>}
                          {vendor.Category && <span>| Cat: {vendor.Category}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT PANEL: Visual Quill Editor Form */}
          <div style={styles.emailRightCard}>
            <div style={{ padding: "16px", borderBottom: "1px solid #E4E7E9", background: "#FAFBFB", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#14181C", margin: 0 }}>Compose Visual Email &amp; Attach File</h3>
              <button type="button" onClick={() => setShowTemplateDrawer(true)} style={styles.quickLoadBtn}>
                ⚡ Choose Template
              </button>
            </div>

            <form onSubmit={handleSendEmails} style={{ maxHeight: "550px", overflowY: "auto", padding: "20px" }}>
              <div style={{ marginBottom: "16px" }}>
                <label style={styles.label}>Subject line</label>
                <input 
                  type="text" required placeholder="Enter subject line..." value={subject}
                  onChange={(e) => setSubject(e.target.value)} className="ap-input" style={styles.input}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={styles.label}>Attach PDF / Document</label>
                <input 
                  type="file" onChange={(e) => setAttachmentFile(e.target.files[0])}
                  style={{ fontSize: "13px", padding: "4px 0", color: "#3C4550", cursor: "pointer", width: "100%" }}
                />
                {attachmentFile && (<p style={{ fontSize: "12px", color: "#14524A", marginTop: "4px" }}>Attached: {attachmentFile.name}</p>)}
              </div>

              <div style={{ marginBottom: "24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "7px", flexWrap: "wrap", gap: "6px" }}>
                  <label style={{ ...styles.label, marginBottom: 0 }}>Email Body &amp; Styling</label>
                  <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                    <button type="button" onClick={() => insertToken('{{Company_Name}}')} style={styles.tokenBtn}>+ Company Name</button>
                    <button type="button" onClick={() => insertToken('{{Contact_Person}}')} style={styles.tokenBtn}>+ Contact Person</button>
                    <button type="button" onClick={() => insertToken('{{GST_Number}}')} style={styles.tokenBtn}>+ GST No</button>
                    <button type="button" onClick={handleInsertMyDetails} style={{ ...styles.tokenBtn, background: "#14524A", color: "#fff", borderColor: "#14524A" }}>+ Add My Details</button>
                  </div>
                </div>

                <div style={{ background: "#FFFFFF", borderRadius: "8px", overflow: "hidden" }}>
                  <div ref={quillRef} style={{ height: "220px", marginBottom: "42px" }} />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <button type="button" onClick={() => { setTemplateNameInput(subject); setShowTemplateModal(true); }} style={styles.secondaryActionBtnDark}>
                  Save as Template
                </button>

                <button 
                  type="submit" disabled={loading || selectedIds.length === 0}
                  style={{ ...styles.primaryBtn, width: "auto", padding: "10px 22px", opacity: loading || selectedIds.length === 0 ? 0.6 : 1, cursor: loading || selectedIds.length === 0 ? "not-allowed" : "pointer" }}
                >
                  {loading ? 'Processing Dispatch...' : `Send Styled Email (${selectedIds.length})`}
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>

      {/* TEMPLATE DRAWER */}
      {showTemplateDrawer && (
        <div style={styles.modalBackdrop}>
          <div style={styles.drawerCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E4E7E9', paddingBottom: '12px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#14181C' }}>Saved Email Templates &amp; Drafts</h3>
              <button onClick={() => setShowTemplateDrawer(false)} style={styles.closeIconBtn}>✕</button>
            </div>
            
            <div style={{ maxHeight: '380px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {savedTemplates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#9AA5AF', fontSize: '13.5px' }}>No saved templates found yet.</div>
              ) : (
                savedTemplates.map((tmpl) => (
                  <div key={tmpl.id || tmpl.subject} style={styles.templateItemCard}>
                    <div style={{ flex: 1, paddingRight: '10px' }}>
                      <div style={{ fontWeight: 600, fontSize: '13.5px', color: '#14524A' }}>{tmpl.name}</div>
                      <div style={{ fontSize: '12.5px', color: '#3C4550', margin: '3px 0' }}><strong>Subject:</strong> {tmpl.subject}</div>
                    </div>
                    <button type="button" onClick={() => handleApplyTemplate(tmpl)} style={styles.useTemplateBtn}>Use Template</button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* SAVE TEMPLATE MODAL */}
      {showTemplateModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalCard}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 700, color: '#14181C' }}>Save Email Template</h3>
            <form onSubmit={handleSaveTemplateSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Template Title / Name</label>
                <input 
                  type="text" required value={templateNameInput} onChange={(e) => setTemplateNameInput(e.target.value)}
                  placeholder="e.g. GST & Functionality Update" style={styles.input} className="ap-input"
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={() => setShowTemplateModal(false)} style={styles.cancelModalBtn}>Cancel</button>
                <button type="submit" style={styles.primaryBtn}>Save Template</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function GlobalStyle() {
  return (
    <style>{`
      * { box-sizing: border-box; }
      body { font-family: 'Inter', -apple-system, 'Segoe UI', sans-serif; margin: 0; background: #F2F4F5; }
      .ap-input:focus { outline: none; border-color: #14524A !important; box-shadow: 0 0 0 3px rgba(20, 82, 74, 0.12); }
      .ql-container { font-family: 'Inter', sans-serif; font-size: 14px; }
      @media (max-width: 768px) { .ap-email-grid { grid-template-columns: 1fr !important; } }
    `}</style>
  );
}

const FONT = "'Inter', -apple-system, 'Segoe UI', sans-serif";

const styles = {
  dashboardContainer: { minHeight: "100vh", backgroundColor: "#F2F4F5", fontFamily: FONT },
  topBar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 28px", background: "#14524A", color: "#fff" },
  topBarLeft: { display: "flex", alignItems: "center", gap: "12px" },
  brandMarkSmall: { width: "34px", height: "34px", borderRadius: "8px", background: "rgba(255,255,255,0.14)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "13px" },
  topBarTitle: { fontSize: "15px", fontWeight: 700 },
  topBarSubtitle: { fontSize: "12px", color: "rgba(255,255,255,0.7)" },
  logoutBtn: { display: "flex", alignItems: "center", gap: "7px", background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", padding: "8px 14px", borderRadius: "7px", cursor: "pointer", fontSize: "13px" },
  secondaryActionBtn: { display: "flex", alignItems: "center", gap: "7px", background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.4)", padding: "8px 14px", borderRadius: "7px", cursor: "pointer", fontSize: "13px", fontWeight: 500 },
  secondaryActionBtnDark: { background: "#F2F4F5", color: "#3C4550", border: "1px solid #D8DCE0", padding: "10px 16px", borderRadius: "8px", cursor: "pointer", fontSize: "13.5px", fontWeight: 600 },
  saveTemplateTopBtn: { background: "#E4EEEC", color: "#14524A", border: "1px solid #14524A", padding: "8px 14px", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 },
  quickLoadBtn: { background: "#E4EEEC", color: "#14524A", border: "none", padding: "5px 10px", borderRadius: "5px", fontSize: "12px", fontWeight: 600, cursor: "pointer" },
  pageBody: { padding: "28px", maxWidth: "1280px", margin: "0 auto" },
  label: { display: "block", fontSize: "13px", fontWeight: 600, color: "#3C4550", marginBottom: "7px" },
  input: { width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #D8DCE0", fontSize: "14px", fontFamily: FONT, background: "#FFFFFF" },
  primaryBtn: { padding: "10px 20px", background: "#14524A", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "14px", fontFamily: FONT },
  emailDispatchGrid: { display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: "20px" },
  emailLeftCard: { background: "#fff", borderRadius: "12px", border: "1px solid #E4E7E9", overflow: "hidden", height: "fit-content" },
  emailRightCard: { background: "#fff", borderRadius: "12px", border: "1px solid #E4E7E9", overflow: "hidden", height: "fit-content" },
  tokenBtn: { background: "#F2F4F5", border: "1px solid #D8DCE0", color: "#3C4550", padding: "4px 9px", borderRadius: "5px", fontSize: "11.5px", cursor: "pointer", fontWeight: 500 },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  drawerCard: { background: '#fff', width: '90%', maxWidth: '520px', borderRadius: '12px', padding: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)' },
  modalCard: { background: '#fff', width: '90%', maxWidth: '440px', borderRadius: '12px', padding: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)' },
  closeIconBtn: { background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#788693' },
  templateItemCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#FAFBFB', border: '1px solid #E4E7E9', borderRadius: '8px' },
  useTemplateBtn: { background: '#14524A', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  cancelModalBtn: { background: 'transparent', color: '#5B6570', border: '1px solid #D8DCE0', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }
};